import React, { useState, useContext, useEffect } from 'react';
import { useTheme } from '@material-ui/core';
import './styles/EllisAiDntChat.css';
import { useStyles } from './styles/index';
import { useAgents } from './hooks/useAgents';
import { useChatHistory } from './hooks/useChatHistory';
import { useChatBotPrompts } from './hooks/useChatBotPrompts';
import { useChat } from './hooks/useChat';
import { useChatBotActions } from './hooks/useChatBotActions';
import { useSuggestedPrompts } from './hooks/useSuggestedPrompts';
import { useEllisPrompts } from './hooks/useEllisPrompts';
import { useEllisDTFlag } from './hooks/useEllisDTFlag';
import { validateFileSize, validateFileType } from './config/attachmentConfig';
import { EmeraldAiChatBot } from '@emerald-react/ai-chat-bot';
import { AuthContext } from '../../providers/AuthProvider';
import { LeftSidebar } from './components/LeftSidebar';
import type { ChatMessage, AgentType } from './types/chat';

// Type alias for backward compatibility with existing code
type Message = ChatMessage;

export default function EllisAiDntChat() {
  const theme = useTheme();
  const isDarkMode = theme.palette.type === 'dark';
  const classes = useStyles();
  const [messages, setMessages] = useState<Message[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Use agent hook for agent-related data and state
  const { agents, currentAgent, setCurrentAgent } = useAgents();

  const { profEmail } = useContext(AuthContext);

  // Use treatment flag hook for feature visibility control
  const { promptLibraryEnabled, attachmentsEnabled } = useEllisDTFlag();

  // Use chat history hook for history management
  const {
    chatHistory,
    currentChatId: _currentChatId,
    setCurrentChatId,
    addToHistory,
    loadChatHistory,
    updateChatMessages,
    deleteFromHistory,
  } = useChatHistory(profEmail);

  // Sync messages to chat history whenever they change
  useEffect(() => {
    if (_currentChatId && messages.length > 0) {
      updateChatMessages(_currentChatId, messages);
    }
  }, [messages, _currentChatId, updateChatMessages]);

  // Fetch prompts for Ellis from the store using environment and tool-specific scopes
  const [rawPrompts, , createPrompt] = useEllisPrompts(currentAgent);

  // Transform raw prompts to ChatBot-ready format
  const promptCards = useChatBotPrompts(rawPrompts, profEmail);

  // Get suggested prompts based on the current agent
  const suggestedPrompts = useSuggestedPrompts(currentAgent);

  // Get agent name for error messages
  const agentName =
    agents.find(a => a.id === currentAgent)?.name || 'Assistant';

  // Use chat hook for message handling and streaming
  const { sendMessage, isLoading, thinkingText, stopMessage } = useChat({
    setMessages,
    messages,
    currentAgent,
    agentName,
  });

  // Use chatbot actions hook for all chat interactions
  const {
    handleSendMessage,
    handleNewChat,
    handleSelectAgent,
    handleLoadChatHistory,
    handleRegenerate,
    handleEdit,
  } = useChatBotActions({
    messages,
    setMessages,
    currentAgent,
    setCurrentAgent,
    setCurrentChatId,
    addToHistory,
    loadChatHistory,
    sendMessage,
    stopMessage,
    agents,
    updateChatMessages,
    userEmail: profEmail,
  });

  const handleDeleteChat = (chatId: string) => {
    deleteFromHistory(chatId);
  };

  const handleOnFileUpload = (files: File[]) => {
    // Validate files during upload
    const validationErrors: string[] = [];
    files.forEach(file => {
      const sizeError = validateFileSize(file);
      if (sizeError) validationErrors.push(sizeError);

      const typeError = validateFileType(file);
      if (typeError) validationErrors.push(typeError);
    });

    if (validationErrors.length > 0) {
      setValidationErrors(validationErrors);
    } else {
      setValidationErrors([]); // Clear errors if all files are valid
    }
    console.log('Files uploaded:', files);
  };

  const handleValidationError = (errors: string[]) => {
    // Show errors above input box
    setValidationErrors(errors);
  };

  return (
    <div
      className={`ellis-ai-chat ${classes.root} ${isDarkMode ? 'dark' : ''}`}
      data-color-mode={isDarkMode ? 'dark' : 'light'}
      style={{ display: 'flex', height: '100%', flexDirection: 'column' }}
    >
      <div style={{ display: 'flex', flex: 1 }}>
        {/* Left Sidebar */}
        <LeftSidebar
          agents={agents.map(agent => ({
            id: agent.id,
            name: agent.name,
            description: agent.description,
            avatar: agent.avatar || '',
            avatarBg: agent.avatarBg || '#f0f0f0',
            avatarImage: agent.avatarImage,
          }))}
          currentAgent={currentAgent}
          chatHistory={chatHistory.map(chat => ({
            id: chat.id,
            title: chat.title,
            timestamp: chat.timestamp,
            agent: chat.agent,
          }))}
          currentChatId={_currentChatId}
          onNewChat={handleNewChat}
          onSelectAgent={handleSelectAgent}
          onLoadChatHistory={handleLoadChatHistory}
          onDeleteChat={handleDeleteChat}
          isDarkMode={isDarkMode}
        />

        {/* Emerald AI Chat Bot */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <EmeraldAiChatBot
            title={
              agents.find(a => a.id === currentAgent)?.name || 'Ellis AI Chat'
            }
            headerIcon={null}
            messages={messages.map(msg => ({
              content: msg.content,
              role: msg.role as any,
              timestamp:
                msg.timestamp instanceof Date
                  ? msg.timestamp
                  : new Date(msg.timestamp),
              attachments: msg.attachments,
            }))}
            isAiThinking={isLoading}
            thinkingStatusText={thinkingText || undefined}
            onSend={(message, attachments) =>
              handleSendMessage(message, attachments)
            }
            onMessageRegenerate={handleRegenerate}
            onMessageEdit={handleEdit}
            onStop={stopMessage}
            suggestedPrompts={suggestedPrompts}
            enableFileUpload={attachmentsEnabled}
            screenSize="full"
            errorMessages={validationErrors}
            welcomeMessage={`Hello, ${
              profEmail?.split('.')[0] || 'there'
            }! How can I help you today?`}
            headerButtonsVisibility={{
              newChat: false,
              screenToggle: false,
              close: false,
            }}
            promptsLibraryProps={
              promptLibraryEnabled
                ? {
                    title: 'Prompt library',
                    activeTab: 'all',
                    showCreateButton: true,
                    prompts: promptCards,
                    showPromptActions: false,
                    onCreatePrompt: createPrompt,
                  }
                : undefined
            }
            onFileUpload={handleOnFileUpload}
          />
        </div>
      </div>
    </div>
  );
}
